import type { SubscriptionTier } from "@/types/projects";

export type TimelineLinkKind = "entity" | "lore" | "chapter";
export type TimelineViewMode = "list" | "graphical";
export type TimelinePathKind = "ramified";
export type TimelineMarkerShape = "circle" | "rect";
export type TimelineSummaryRequestState = "idle" | "previewing" | "approving";
export type TimelineBranchDirection = "up" | "down";

export interface TimelineEventDraft {
  description: string;
  eraLabel: string;
  title: string;
}

export interface TimelineLimitPrompt {
  reason: "timeline_event_limit_reached";
  tier: SubscriptionTier;
  currentCount: number;
  allowedCount: number;
  recommendedTier: SubscriptionTier;
}

export interface TimelineCapacitySummary {
  eventsLimit: number | null;
  eventsRemaining: number | null;
  eventsUsed: number;
  isUnlimited: boolean;
  tier: SubscriptionTier;
}

export interface TimelineLinkTargetOption {
  id: string;
  kind: TimelineLinkKind;
  kindLabelKey: string;
  label: string;
  secondaryText: string;
}

export interface TimelineEventLinkView {
  broken: boolean;
  id: string;
  label: string;
  linkedId: string;
  linkedKind: TimelineLinkKind;
  linkedKindLabelKey: string;
  openHref: string | null;
  repairHintKey: string | null;
  secondaryText: string;
}

export interface TimelineEventView {
  aiSummary: string | null;
  aiSummaryGeneratedAt: string | null;
  brokenLinkCount: number;
  createdAt: string;
  description: string;
  descriptionPreview: string;
  eraLabel: string;
  hasApprovedSummary: boolean;
  id: string;
  imagePath?: string | null;
  imageUrl?: string | null;
  linkCount: number;
  links: TimelineEventLinkView[];
  projectId: string;
  sortOrder: number;
  title: string;
  updatedAt: string;
}

export interface TimelineWorkspaceSnapshot {
  eventCapacity: TimelineCapacitySummary;
  events: TimelineEventView[];
  linkOptions: TimelineLinkTargetOption[];
  projectId: string;
  projectTitle: string;
  tier: SubscriptionTier;
}

export interface TimelineSummaryCitation {
  description: string;
  title: string;
}

export interface TimelineSummaryPreview {
  approvalToken: string;
  citation: TimelineSummaryCitation;
  eventId: string;
  expiresAt: string;
  status: "preview";
  summary: string;
}

export interface TimelineSummaryApprovalResult {
  creditsRemaining: number | null;
  eventId: string;
  generatedAt: string;
  status: "approved";
  summary: string;
}

export interface TimelineGraphMarkerView {
  branchColor: string;
  branchPathD: string;
  branchSide: "left" | "right";
  cardHeight: number;
  cardWidth: number;
  cardX: number;
  cardY: number;
  direction: TimelineBranchDirection;
  eraLabel: string;
  eventId: string;
  index: number;
  isSelected: boolean;
  linkLabel: string | null;
  markerShape: TimelineMarkerShape;
  milestoneLabel: string;
  summaryLine: string | null;
  trunkX: number;
  title: string;
  x: number;
  y: number;
}

export interface TimelineGraphMilestoneView {
  label: string;
  x: number;
}

export interface TimelineGraphLayout {
  axisY: number;
  height: number;
  markers: TimelineGraphMarkerView[];
  milestones: TimelineGraphMilestoneView[];
  pathKind: TimelinePathKind;
  width: number;
}

export const timelineViewModes: TimelineViewMode[] = ["list", "graphical"];

export function createTimelineDraft(
  overrides: Partial<TimelineEventDraft> = {},
): TimelineEventDraft {
  return {
    description: overrides.description ?? "",
    eraLabel: overrides.eraLabel ?? "",
    title: overrides.title ?? "",
  };
}

export function getTimelineChapterHref(projectId: string, chapterId: string) {
  const search = new URLSearchParams({
    chapterId,
  });

  return `/projects/${projectId}/stories?${search.toString()}`;
}

export function getTimelineLinkKindLabelKey(kind: TimelineLinkKind) {
  switch (kind) {
    case "entity":
      return "timeline.links.kinds.entity";
    case "lore":
      return "timeline.links.kinds.lore";
    case "chapter":
      return "timeline.links.kinds.chapter";
  }
}

export function getTimelineSummaryStatusKey(tier: SubscriptionTier, event: TimelineEventView) {
  if (tier === "free") {
    return "timeline.summary.lockedBody";
  }

  if (event.hasApprovedSummary && event.aiSummary) {
    return "timeline.summary.cachedBody";
  }

  return "timeline.summary.emptyBody";
}

export function hasVisibleTimelineSummary(
  tier: SubscriptionTier,
  event: Pick<TimelineEventView, "aiSummary">,
) {
  return tier !== "free" && Boolean(event.aiSummary);
}
